import type { GrowthCategory } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import {
  EventTypes,
  EventTypes as CoreEventTypes,
  PageSource,
  track,
  trackGoldStandardEvent,
} from '@mc/track-event'
import {
  ActionTypes,
  KeyTypes,
  saveUserData,
  getTrackedId,
} from '@mc/user-data'
import { setMetaProductIdCookie } from 'libs/cookies/metaProductIdCookie'
import { CourseMeta, Outcome, OutcomeContent } from 'types/data'

type FeaturedInstructor = {
  name: string
  imageUrl: string
}

const getInstructorImage = (courseMeta: CourseMeta) => ({
  name: courseMeta.instructorName,
  imageUrl: courseMeta.instructorImgSrc,
})

export const getFeaturedInstructors = (
  outcome: Outcome,
  outcomeRankedContent: OutcomeContent[],
  courseSlugToMeta: Record<string, CourseMeta>,
) => {
  //  https://masterclass-dev.atlassian.net/browse/PEN-5068
  if (outcome.slug === 'harness-control-with-strategic-calm') {
    const df = courseSlugToMeta['donna-farhi-teaches-yoga-foundations']
    const cv = courseSlugToMeta['chris-voss-teaches-the-art-of-negotiation']
    return [getInstructorImage(df), getInstructorImage(cv)]
  }
  //  https://masterclass-dev.atlassian.net/browse/PEN-5068
  if (outcome.slug === 'build-resilience-for-growth') {
    const lh = courseSlugToMeta['lewis-hamilton-teaches-a-winning-mindset']
    const rro =
      courseSlugToMeta[
        'robin-roberts-teaches-effective-and-authentic-communication'
      ]
    return [getInstructorImage(lh), getInstructorImage(rro)]
  }

  // default logic: top ranked courses
  return getTopRankedFeaturedInstructors(outcomeRankedContent, courseSlugToMeta)
}

export const getTopRankedFeaturedInstructors = (
  outcomeRankedContent: OutcomeContent[],
  courseSlugToMeta: Record<string, CourseMeta>,
) =>
  outcomeRankedContent.reduce<FeaturedInstructor[]>((acc, { courseSlug }) => {
    if (courseSlugToMeta[courseSlug]) {
      const instructor = getInstructorImage(courseSlugToMeta[courseSlug])
      if (!acc.some((inst) => inst.name === instructor.name)) {
        return [...acc, instructor]
      }
    }
    return acc
  }, [])

export const useOutcomePageEvents = () => {
  const { outcome, outcomeSlug, primaryCategory, primarySubcategory } =
    useStaticData<{
      outcomeSlug: string
      outcome: Outcome
      primarySubcategory: GrowthCategory
      primaryCategory: GrowthCategory
    }>()

  const getPageCategoryInfo = () => ({
    categoryId: primaryCategory.id,
    categoryName: primaryCategory.name,
    subcategoryId: primarySubcategory.id,
    subcategoryName: primarySubcategory.name,
    outcomeSlug,
    outcomeName: outcome.name,
  })

  const trackOutcomePageEvent = (
    eventName: string,
    eventProps: Record<string, string | number | undefined>,
  ) => {
    track(eventName, {
      ...eventProps,
      ...getPageCategoryInfo(),
      page_source: PageSource.OUTCOME,
    })
  }

  const trackProductListViewed = () => {
    const catId = getTrackedId({
      key: KeyTypes.CATEGORY,
      id: getPageCategoryInfo().categoryId,
    })
    const subcatId = getTrackedId({
      key: KeyTypes.SUBCATEGORY,
      id: getPageCategoryInfo().subcategoryId,
    })
    const outcomeId = getTrackedId({
      key: KeyTypes.OUTCOME,
      id: outcome.id,
    })

    const eventDetails = {
      category: catId || '0',
      products: [{ product_id: outcomeId }],
      stored_category_id: catId,
      stored_subcategory_id: subcatId,
      stored_outcome_id: outcomeId,
    }

    trackGoldStandardEvent(
      CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
      eventDetails,
    )

    saveUserData({
      key: KeyTypes.CATEGORY,
      id: catId,
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })

    saveUserData({
      key: KeyTypes.SUBCATEGORY,
      id: subcatId,
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })

    saveUserData({
      key: KeyTypes.OUTCOME,
      id: outcome.id.toString(),
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })

    setMetaProductIdCookie(outcomeId)
  }

  const trackBreadcrumbClicked = (
    entityName: string,
    entityType: 'category' | 'subcategory',
  ) => {
    trackOutcomePageEvent(EventTypes.OUTCOME_PAGE_CLICK, {
      action: entityName,
      location: 'breadcrumbs',
      category: entityType,
    })
  }

  const trackComponentViewed = (location: string) => {
    track(EventTypes.OUTCOME_PAGE_ROW_VIEWED, {
      location,
      ...getPageCategoryInfo(),
      page_source: PageSource.OUTCOME,
    })
  }

  const trackOutcomeTileClicked = ({
    action,
    outcomeName,
    category,
    tileClass,
    location,
  }: {
    action: string
    outcomeName: string
    category: string
    tileClass: string
    location: string
  }) => {
    trackOutcomePageEvent(EventTypes.OUTCOME_PAGE_CLICK, {
      action,
      category,
      class: tileClass,
      location,
      outcomeName,
    })
  }

  return {
    getPageCategoryInfo,
    trackProductListViewed,
    trackBreadcrumbClicked,
    trackComponentViewed,
    trackOutcomeTileClicked,
  }
}
